import { ALLOWED_FILE_MIME } from '@clim/domain';

const MAX_SIDE = 2000;

/**
 * Prepares a photo for sending: resized (max 2000 px) and re-encoded in JPEG, which also
 * removes EXIF metadata (GPS position of the phone…). Formats the browser cannot decode
 * (e.g. HEIC on Chrome) are sent as they are.
 */
export async function preparePhoto(file: File): Promise<{ data: Uint8Array; mime: string; name: string }> {
  const original = async () => ({ data: new Uint8Array(await file.arrayBuffer()), mime: file.type || 'application/octet-stream', name: file.name });
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    return original();
  }
  const scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.85));
  if (!blob) return original();
  return { data: new Uint8Array(await blob.arrayBuffer()), mime: 'image/jpeg', name: file.name.replace(/\.[^.]+$/, '') + '.jpg' };
}

export async function readDocument(file: File): Promise<{ data: Uint8Array; mime: string; name: string }> {
  if (file.type.startsWith('image/')) return preparePhoto(file);
  return { data: new Uint8Array(await file.arrayBuffer()), mime: file.type, name: file.name };
}

export const isAllowedMime = (m: string) => (ALLOWED_FILE_MIME as readonly string[]).includes(m);
